import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { signInWithCustomToken, onIdTokenChanged } from 'firebase/auth';
import { auth } from '@/utils/firebase';
import {
	authorisationApi,
	useGetUserFirebaseTokenQuery,
} from '@/features/authorisation/authorisation';
import {
	clearFirebaseToken,
	setFirebaseToken,
	setFirebaseReady,
	setSignInError,
} from '@/features/authorisationSlice/authorisationSlice';
import { setAccessToken } from '@/features/msalAccount/msalAccountSlice';
import { scopes } from '@/utils/msGraphUtils';
import { secureCookieOptions } from '@/utils/secureCookieOptions';

const extractCustomToken = (data) => {
	const raw = data?.fbToken;
	if (typeof raw === 'string' && raw.length > 0) {
		return raw;
	}
	if (raw && typeof raw === 'object') {
		const nested = raw.fbToken || raw.token;
		if (typeof nested === 'string' && nested.length > 0) {
			return nested;
		}
	}
	return null;
};

const resolveMsToken = (accessToken) => {
	if (typeof accessToken === 'string' && accessToken.length > 0) {
		return accessToken;
	}
	const cookieToken = Cookies.get('token');
	return typeof cookieToken === 'string' && cookieToken.length > 0 ? cookieToken : null;
};

export const FirebaseTokenProvider = ({ children }) => {
	const dispatch = useDispatch();
	const { accounts, instance, inProgress } = useMsal();
	const msAccessToken = useSelector((state) => state.msalAccount.accessToken);
	const expiration = useSelector((state) => state.msalAccount.expiration);
	const hasMsAccount = accounts.length > 0;
	const msToken = resolveMsToken(msAccessToken);
	// Gate on a real MS token (Redux string or cookie), not a render-time cookie-only read.
	// Cookie-only gating hung on first boot: AuthenticatedWrapper sets the cookie async and
	// Cookies.get() does not trigger a re-render by itself.
	// Same expiry window as PageLayout. An expired cookie makes Graph reject createFirebaseToken
	// with InvalidAuthenticationToken (invalid token lifetime).
	const hasValidMsToken =
		Boolean(msToken) && Boolean(expiration) && expiration - Date.now() > 2 * 60000;
	const isMsLoggedIn = hasMsAccount && hasValidMsToken;
	const firebaseReady = useSelector((state) => state.firebase.firebaseReady);
	const brokerGroupId = useSelector((state) => {
		const email = state?.msalAccount?.account?.username;
		if (!email) return null;
		const userQueryKey = `getUserDetails({"email":"${email}"})`;
		return state?.userApi?.queries?.[userQueryKey]?.data?.brokerGroupId ?? null;
	});
	const lastBrokerGroupIdRef = useRef(null);
	const lastCustomTokenRef = useRef(null);
	const acquiringMsTokenRef = useRef(false);

	const { data, error, isLoading, isFetching, refetch } = useGetUserFirebaseTokenQuery(
		{},
		{
			skip: !isMsLoggedIn,
			// Always re-mint on mount so brokerage claims are not served from a stale cache.
			refetchOnMountOrArgChange: true,
		},
	);

	// Ensure an MS token exists for create-token (first boot / cleared cookies).
	// Uses MSAL accounts directly so we do not wait on Redux account sync timing.
	// Wait for MsalProvider to finish initialize(): accounts come from cache before that.
	useEffect(() => {
		if (
			inProgress !== InteractionStatus.None ||
			!hasMsAccount ||
			isMsLoggedIn ||
			acquiringMsTokenRef.current
		) {
			return undefined;
		}

		const account = accounts[0];
		if (!account) {
			return undefined;
		}

		let cancelled = false;
		acquiringMsTokenRef.current = true;

		const ensureMsToken = async () => {
			try {
				if (!instance.getActiveAccount()) {
					instance.setActiveAccount(account);
				}
				const response = await instance.acquireTokenSilent({
					scopes,
					account,
				});
				if (cancelled) {
					return;
				}
				const token = response?.accessToken;
				if (!token) {
					return;
				}
				const expiration = response.expiresOn
					? new Date(response.expiresOn).getTime()
					: null;
				Cookies.set('token', token, secureCookieOptions);
				dispatch(setAccessToken({ accessToken: token, expiration }));
			} catch (err) {
				console.error('[Firebase] Failed to acquire MS token for create-token:', err);
			} finally {
				acquiringMsTokenRef.current = false;
			}
		};

		ensureMsToken();
		return () => {
			cancelled = true;
			acquiringMsTokenRef.current = false;
		};
	}, [inProgress, hasMsAccount, isMsLoggedIn, accounts, instance, dispatch]);

	// Keep Redux in sync whenever Firebase refreshes the ID token.
	useEffect(() => {
		const unsubscribe = onIdTokenChanged(auth, async (user) => {
			if (!user) {
				dispatch(clearFirebaseToken());
				return;
			}

			try {
				const token = await user.getIdToken();
				dispatch(setFirebaseToken(token));
			} catch (err) {
				console.error('[Firebase] Failed to read refreshed ID token:', err);
			}
		});

		return unsubscribe;
	}, [dispatch]);

	// Re-mint when brokerage changes so custom claims do not stay stale.
	useEffect(() => {
		if (!firebaseReady || !brokerGroupId) {
			return;
		}
		if (lastBrokerGroupIdRef.current == null) {
			lastBrokerGroupIdRef.current = brokerGroupId;
			return;
		}
		if (lastBrokerGroupIdRef.current === brokerGroupId) {
			return;
		}
		lastBrokerGroupIdRef.current = brokerGroupId;
		lastCustomTokenRef.current = null;
		dispatch(authorisationApi.util.invalidateTags(['AUTHORISATION']));
		refetch();
	}, [brokerGroupId, firebaseReady, dispatch, refetch]);

	useEffect(() => {
		// Wait until MS session + create-token response are ready.
		if (!isMsLoggedIn || isLoading || isFetching) {
			return undefined;
		}

		const customToken = extractCustomToken(data);
		// An admin turned this user off: say so, instead of loading forever
		dispatch(setSignInError(error?.data?.code === 'USER_INACTIVE' ? error.data.message : null));
		if (error || !customToken) {
			console.error('Error loading Firebase token:', error ?? 'no fbToken in response');
			dispatch(clearFirebaseToken());
			dispatch(setFirebaseReady(false));
			return undefined;
		}

		// Avoid re-signing with the exact same custom token (e.g. StrictMode remount).
		if (lastCustomTokenRef.current === customToken && auth.currentUser) {
			dispatch(setFirebaseReady(true));
			return undefined;
		}

		let cancelled = false;

		const signInFirebase = async () => {
			try {
				// Always sign in with the mint response so brokerage/custom claims refresh.
				// Do not reuse auth.currentUser alone — IndexedDB persistence keeps old claims.
				const userCredential = await signInWithCustomToken(auth, customToken);
				if (cancelled) {
					return;
				}
				const token = await userCredential.user.getIdToken(true);
				if (cancelled) {
					return;
				}
				lastCustomTokenRef.current = customToken;
				dispatch(setFirebaseToken(token));
				dispatch(setFirebaseReady(true));
			} catch (err) {
				console.error('[Firebase] Sign in failed:', err);
				if (cancelled) {
					return;
				}
				dispatch(clearFirebaseToken());
				dispatch(setFirebaseReady(false));
			}
		};

		signInFirebase();
		return () => {
			cancelled = true;
		};
	}, [data, error, isLoading, isFetching, isMsLoggedIn, dispatch]);

	return children;
};
